<template>
    <div class="import-manager" v-if="importManifest">

        <!-- Header -->
        <div class="row">
            <div class="col-sm-12 col-md-6 col-lg-8 form-group">
                <div class="row">
                    <div class="col-sm-6 col-md-4 form-group" v-if="importManifest.created_at">
                        <dl class="no-margin">
                            <dt>Import File</dt>
                            <dd class="truncate">
                                <a v-for="file in importManifest.files" :href="'/files/' + file.id + '/download'"
                                   :key="file.id">
                                    {{file.name}}
                                </a>
                            </dd>
                        </dl>
                    </div>

                    <div v-if="importManifest.created_at && importManifest.archive_enrolled"
                         class="col-sm-12 col-md-4 form-group">
                        <ul class="fa-ul margin-x-bottom">
                            <li>
                                <i class="fa-li fa fa-check text-green"></i> Archive Enrolled
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div class="col-sm-12 col-md-6 col-lg-4 form-group align-md-right" :class="{loading: loadingAll}">
                <button v-if="importManifest.data" type="button" class="btn btn-success" @click="saveImportManifest"
                        :disabled="loadingAll">
                    Save
                </button>

                <button v-if="importManifest.data" type="button" class="btn btn-warning"
                        @click="$modal.show('confirm-execute')"
                        :disabled="loadingAll">
                    Execute
                </button>

                <dropdown menu-right class="dropdown-form">
                    <btn class="dropdown-toggle" type="primary">
                        <i class="fa fa-cog" aria-hidden="true"></i>
                    </btn>

                    <template slot="dropdown" class="padding-sm-left">
                        <li v-if="importManifest">
                            <label class="no-margin text-normal">
                                <input type="checkbox" class="margin-sm-bottom"
                                       v-model.boolean="importManifest.archive_enrolled">
                                Archive Enrolled
                            </label>
                        </li>
                        <li>
                            <a role="button" @click="$modal.show('import-columns-modal')" :disabled="loadingAll">
                                Configure
                            </a>
                        </li>
                        <li v-if="importManifest.data">
                            <a role="button" @click="syncImport" :disabled="loadingAll">
                                Sync Matching
                            </a>
                        </li>
                        <li>
                            <a role="button" @click="loadData" :disabled="loadingAll">
                                Load Data From Source
                            </a>
                        </li>
                        <li>
                            <a role="button" @click="deleteImport" :disabled="loadingAll">
                                Delete Import
                            </a>
                        </li>
                    </template>
                </dropdown>
            </div>
        </div>

        <job-monitor-overlay
            class="form-group"
            :job-monitor="jobMonitor"></job-monitor-overlay>

        <div v-if="importManifest.created_at" :class="{loading: loadingAll}">

            <!--Pre Data Config-->
            <div v-if="!importManifest.hasImportColumns || !importManifest.data">
                <div class="text-center">
                    <div v-if="!importManifest.hasImportColumns">
                        <h4 class="margin-md-bottom">Configuration Required</h4>

                        <div class="form-group">
                            <button class="btn btn-primary" type="button" @click="$modal.show('import-columns-modal')"
                                    :disabled="loadingAll">
                                Configure
                            </button>
                        </div>
                    </div>

                    <div v-if="!importManifest.data">
                        <h4 class="margin-md-bottom">No data loaded</h4>

                        <div class="form-group">
                            <button class="btn btn-warning" type="button" @click="loadData" :disabled="loadingAll">
                                Load Data from Source
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <section id="archiving-residents" v-if="importManifest.archiving">
                <header @click="toggleShowArchiving">
                    <h3>Archiving</h3>
                    <h5>
                        {{Object.keys(actualArchiving).length}}/{{Object.keys( importManifest.archiving ).length}}
                        Residents
                    </h5>

                    <i v-if="showArchiving" class="fa fa-caret-up" aria-hidden="true"></i>
                    <i v-else class="fa fa-caret-down" aria-hidden="true"></i>
                </header>

                <div v-if="showArchiving">
                    <div class="text-right">
                        <button class="btn btn-sm btn-link" type="button"
                                @click="setAllExemptResidentIds(importManifest.archiving, 'id')">
                            Select All
                        </button>
                        <button class="btn btn-sm btn-link" type="button"
                                @click="clearExemptResidentIds(importManifest.archiving, 'id')">
                            Select None
                        </button>
                    </div>

                    <table class="table table-striped table-hover table-responsive">
                        <thead>
                        <tr>
                            <th v-if="importManifest.archive_enrolled" class="enrolled-col">
                                Enrolled
                            </th>

                            <th
                                v-for="importColumn in importManifest.applicableImportColumns"
                                :key="importColumn.id"
                                :class="['data-type-' + importColumn.data_type]">

                                <a href="javascript://void(0)"
                                   @click="setSort( 'archiving', importColumn.residentAttributePath)">
                                    {{importColumn.displayAttribute}}

                                    <sort-icon :sort-by="archivingSortBy"
                                               :sort-dir="archivingSortDir"
                                               :sort-value="importColumn.residentAttributePath">
                                    </sort-icon>
                                </a>
                            </th>

                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(resident, index) in archiving" class="pointer" :key="resident.id"
                            :class="{success: importManifest.exempt_resident_ids.includes(resident.id)}"
                            @click="toggleExemptResidentId(resident.id)">

                            <td v-if="importManifest.archive_enrolled" class="enrolled-col">
                                <i class="fa fa-check text-green" v-if="resident.enrolled"></i>
                            </td>

                            <td v-for="importColumn in importManifest.applicableImportColumns"
                                :key="importColumn.id">
                                {{importColumn.modelName === 'resident' ? resident[importColumn.attributeName] :
                                resident.lease[importColumn.attributeName]}}
                            </td>

                            <td class="control-col">
                                <a href="javascript://void(0)"
                                   v-if="importManifest.exempt_resident_ids.includes(resident.id)">Archive</a>
                                <a href="javascript://void(0)" v-else>Keep</a>
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    <div class="text-right">
                        <button class="btn btn-sm btn-link" type="button"
                                @click="setAllExemptResidentIds(archiving, 'id')">
                            Select All
                        </button>
                        <button class="btn btn-sm btn-link" type="button"
                                @click="clearExemptResidentIds(archiving, 'id')">
                            Select None
                        </button>
                    </div>
                </div>
            </section>

            <section id="creating-residents" v-if="importManifest.creating">
                <header @click="toggleShowCreating">
                    <h3>Creating </h3>
                    <h5>
                        {{Object.keys(actualCreating).length}}/{{Object.keys( creating ).length}}
                        Residents
                    </h5>

                    <i v-if="showCreating" class="fa fa-caret-up" aria-hidden="true"></i>
                    <i v-else class="fa fa-caret-down" aria-hidden="true"></i>
                </header>

                <div v-if="showCreating">
                    <div class="text-right">
                        <button class="btn btn-sm btn-link" type="button"
                                @click="setAllExemptDataIds(importManifest.creating)">
                            Select All
                        </button>
                        <button class="btn btn-sm btn-link" type="button"
                                @click="clearExemptDataIds(importManifest.creating)">
                            Select None
                        </button>
                    </div>

                    <table class="table table-striped table-hover table-responsive">
                        <thead>
                        <tr>
                            <th
                                v-for="importColumn in importManifest.applicableImportColumns"
                                :key="importColumn.id"
                                :class="['data-type-' + importColumn.data_type]"
                            >
                                <a href="javascript://void(0)"
                                   @click="setSort( 'creating', importColumn.residentAttributePath)">
                                    {{importColumn.displayAttribute}}

                                    <sort-icon :sort-by="creatingSortBy"
                                               :sort-dir="creatingSortDir"
                                               :sort-value="importColumn.residentAttributePath">
                                    </sort-icon>
                                </a>
                            </th>

                            <th></th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(importModel, index) in creating"
                            :key="index"
                            :class="getRowColorObject(index)">

                            <td v-for="importColumn in importManifest.applicableImportColumns"
                                :key="importColumn.id">
                                {{importColumn.modelName === 'resident' ? importModel[importColumn.attributeName] :
                                importModel.lease[importColumn.attributeName]}}
                            </td>

                            <td class="control-col">
                                <a href="javascript://void(0)" @click="openInspectModal(index)">
                                    Match
                                </a>
                            </td>
                            <td class="control-col">
                                <a href="javascript://void(0)" v-if="index in importManifest.exempt_data_ids"
                                   @click="unsetExemptDataId(index)">
                                    Unmatch
                                </a>
                                <a href="javascript://void(0)" v-else @click="setExemptDataId(index, null)">
                                    Remove
                                </a>
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    <div class="text-right">
                        <button class="btn btn-sm btn-link" type="button"
                                @click="setAllExemptDataIds(importManifest.creating)">
                            Select All
                        </button>
                        <button class="btn btn-sm btn-link" type="button"
                                @click="clearExemptDataIds(importManifest.creating)">
                            Select None
                        </button>
                    </div>
                </div>
            </section>

            <section id="updating-residents" v-if="importManifest.updating">
                <header @click="toggleShowUpdating">
                    <h3>Updating </h3>
                    <h5>
                        {{Object.keys(actualUpdating).length}}/{{Object.keys( updating ).length}}
                        Residents
                    </h5>

                    <i v-if="showUpdating" class="fa fa-caret-up" aria-hidden="true"></i>
                    <i v-else class="fa fa-caret-down" aria-hidden="true"></i>
                </header>

                <div v-if="showUpdating">
                    <div class="text-right">
                        <button class="btn btn-sm btn-link" type="button"
                                @click="setAllExemptDataIds(importManifest.updating)">
                            Select All
                        </button>
                        <button class="btn btn-sm btn-link" type="button"
                                @click="setAllExemptDataIds(importManifest.updating)">
                            Select None
                        </button>
                    </div>

                    <table class="table table-striped table-hover table-responsive">
                        <thead>
                        <tr>
                            <th></th>

                            <th
                                v-for="importColumn in importManifest.applicableImportColumns"
                                :key="importColumn.id"
                                :class="['data-type-' + importColumn.data_type]"
                            >
                                <a href="javascript://void(0)"
                                   @click="setSort( 'updating', 'resident.' + importColumn.residentAttributePath)">
                                    {{importColumn.displayAttribute}}

                                    <sort-icon :sort-by="updatingSortBy"
                                               :sort-dir="updatingSortDir"
                                               :sort-value="'resident.' + importColumn.residentAttributePath">
                                    </sort-icon>
                                </a>
                            </th>

                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(updateData, index) in updating"
                            :key="index"
                            :class="getRowColorObject(index)">
                            <td>
                                <i aria-hidden="true"
                                   class="fa fa-refresh text-green"
                                   v-tooltip="'Restoring'"
                                   v-if="updateData.resident.deleted_at"></i>
                            </td>

                            <update-cell
                                v-for="importColumn in importManifest.applicableImportColumns"
                                :key="importColumn.id"
                                :oldDatum="importColumn.modelName === 'resident' ? updateData.resident[importColumn.attributeName] : updateData.resident.lease[importColumn.attributeName]"
                                :newDatum="importColumn.modelName === 'resident' ? updateData.row[importColumn.attributeName] : updateData.row.lease[importColumn.attributeName]"
                            >
                            </update-cell>

                            <td class="control-col">
                                <a href="javascript://void(0)" @click="openInspectModal(index)">
                                    Match
                                </a>
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    <div class="text-right">
                        <button class="btn btn-sm btn-link" type="button"
                                @click="setAllExemptDataIds(importManifest.updating)">
                            Select All
                        </button>
                        <button class="btn btn-sm btn-link" type="button"
                                @click="setAllExemptDataIds(importManifest.updating)">
                            Select None
                        </button>
                    </div>
                </div>
            </section>
        </div>

        <modal name="import-columns-modal" :adaptive="true" :scrollable="true" height="auto"
               @before-close="init"
               width="80%">
            <div class="dashboard-block">
                <header>
                    Import Configuration

                    <a href="javascript://void(0)" @click="$modal.hide( 'import-columns-modal' )" class="back">Close</a>
                </header>

                <import-columns
                    ref="importColumns"
                    :import-manifest="importManifest">
                </import-columns>
            </div>
        </modal>

        <modal name="inspect-modal" :adaptive="true" :scrollable="true" height="auto"
               @before-close="resetInspect"
               v-if="inspect.index !== null && importManifest.exempt_data_ids"
               :max-width="720"
               width="80%">
            <div class="dashboard-block">
                <form @submit.prevent="setInspect" method="POST"
                      accept-charset="UTF-8"
                      data-vv-scope="inspect"
                      novalidate="novalidate">
                    <header>
                        <a href="javascript://void(0)" @click="$modal.hide( 'inspect-modal' )" class="back">Close</a>
                    </header>

                    <br>

                    <div class="row" v-if="inspectData !== null">
                        <div v-for="{value, importColumn} in inspectData" class="col-lg-3 col-md-4 col-sm-6 form-group">
                            <dl class="no-margin">
                                <dt>{{importColumn.displayAttribute}}</dt>
                                <dd class="truncate">
                                    {{value}}&nbsp;
                                </dd>
                            </dl>
                        </div>
                    </div>

                    <div class="form-group has-feedback"
                         :class="{'has-error': errors.has('inspect.ImportManifest[inspect_match_value]')}">

                        <div class="form-group">
                            <div class="btn-group btn-group-justified" role="group">
                                <div class="btn-group" role="group">
                                    <button type="button"
                                            :class="{ active: inspect.type === 'create' }"
                                            class="btn btn-sm btn-success"
                                            @click="setInspectType('create')">
                                        Create
                                    </button>
                                </div>
                                <div class="btn-group" role="group">
                                    <button type="button"
                                            :class="{ active: inspect.type === 'update' }"
                                            class="btn btn-sm btn-primary"
                                            @click="setInspectType('update')">
                                        Update
                                    </button>
                                </div>
                                <div class="btn-group" role="group">
                                    <button type="button"
                                            :class="{ active: inspect.type === 'skip' }"
                                            class="btn btn-sm btn-danger"
                                            @click="setInspectType('skip')">
                                        Skip Row
                                    </button>
                                </div>
                            </div>
                        </div>

                        <auto-complete
                            @change="setInspectResidentId"
                            class="form-group"
                            name="ImportManifest[inspect_resident_id]"
                            label="Current Match"
                            :initialId="inspect.resident_id"
                            :query-string="'archived=true&limit=5&project_id=' + importManifest.project_id"
                            search-results-index="Residents"
                            find-result-index="Resident"
                            resource-url="/api/residents"
                            ref="residentAutoComplete"
                            v-show="importManifest.id && inspect.type === 'update'">
                            <template slot-scope="resident" slot="model-display">
                                {{resident.first_name}} {{resident.last_name}} {{resident.lease.unit ? '(Apt ' +
                                resident.lease.unit
                                + ')' : ''}} {{resident.deleted_at ? '(archived)' : ''}}
                            </template>
                        </auto-complete>

                        <input type="hidden"
                               v-validate="'required'"
                               name="ImportManifest[inspect_resident_id]"
                               v-model="inspect.resident_id">

                        <span v-show="errors.has('inspect.ImportManifest[inspect_resident_id]')"
                              class="help-block">
                            {{ errors.first('inspect.ImportManifest[inspect_resident_id]') }}
                        </span>

                        <h5 class="margin-sm-bottom" v-if="inspect.type === 'update'">Possible Matches</h5>

                        <div class="list-group" v-if="inspect.type === 'update'">
                            <a class="list-group-item relative selectable"
                               v-for="resident in inspect.residents" :key="resident.id"
                               :class="{'list-group-item-success': inspect.resident_id === resident.id}"
                               @click="setInspectResidentId(resident.id)">
                                <h5>
                                    {{resident.first_name}} {{resident.last_name}} (Apt: {{resident.lease.unit}})
                                </h5>
                                <h6>{{resident.email}} {{resident.deleted_at ? '(archived)' : ''}}</h6>
                            </a>
                        </div>

                        <p v-if="inspect.residents.length === 0">
                            No matches found
                        </p>
                    </div>

                    <hr>

                    <div class="row">
                        <div class="col-xs-6">
                            <button type="button" class="btn btn-danger"
                                    @click="unsetInspect"
                                    v-if="inspect.index in importManifest.exempt_data_ids">
                                Clear Override
                            </button>
                        </div>
                        <div class="col-xs-6">
                            <button type="submit" class="btn btn-block btn-success" :disabled="loadingAll">
                                Save
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </modal>

        <modal name="confirm-execute" :adaptive="true" :scrollable="true" height="auto">
            <div class="dashboard-block">
                <header>
                    <h4>Are You Sure?</h4>
                </header>

                <div class="form-group">
                    <dl>
                        <dt>Archiving</dt>
                        <dd>
                            {{Object.keys(actualArchiving).length}}/{{Object.keys( archiving ).length}}
                            Residents
                        </dd>

                        <dt>Updating</dt>
                        <dd>{{Object.keys(actualUpdating).length}}/{{Object.keys( updating ).length}}
                            Residents
                        </dd>

                        <dt>Creating</dt>
                        <dd>{{Object.keys(actualCreating).length}}/{{Object.keys( creating ).length}}
                            Residents
                        </dd>
                    </dl>
                </div>

                <div class="row">
                    <div class="col-xs-6">
                        <button type="button" class="btn btn-default" @click="$modal.hide('confirm-execute')">
                            Cancel
                        </button>
                    </div>
                    <div class="col-xs-6 text-right">
                        <button role="button" @click="executeImport" :disabled="loadingAll"
                                class="btn btn-success">
                            Execute Import
                        </button>
                    </div>
                </div>
            </div>
        </modal>
    </div>
</template>


<script>
    import ImportColumns from "./ImportColumns";
    import JobMonitorOverlay from "./JobMonitorOverlay";

    export default {
        components: { JobMonitorOverlay, ImportColumns },

        props: {
            modalId: {
                default: Math.random().toString(),
                type: String
            },

            importManifestId: {
                type: String,
                required: true,
            },
        },

        data(){
            return {
                archivingSortBy: 'last_name',
                archivingSortDir: 'ASC',

                updatingSortBy: 'last_name',
                updatingSortDir: 'ASC',

                creatingSortBy: 'last_name',
                creatingSortDir: 'ASC',

                sourceSortBy: 'last_name',
                sourceSortDir: 'ASC',

                importManifest: {},

                jobMonitor: {},
                loading: true,

                showCreating: false,
                showUpdating: false,
                showArchiving: false,

                inspect: {
                    loading: true,
                    index: null,
                    resident_id: null,
                    residents: [],
                    type: 'create', // create, update, skip
                },
            };
        },
        mounted(){
            this.init();
        },

        computed: {
            inspectData(){
                if( this.inspect.index && this.importManifest.data[ this.inspect.index ] ){
                    let importModel = this.importManifest.data[ this.inspect.index ];

                    return _.map( this.importManifest.applicableImportColumns, ( importColumn ) => {
                        return {
                            value: _.get( importModel, importColumn.residentAttributePath ),
                            importColumn: importColumn
                        };
                    } );
                }

                return null;
            },
            inspectValue(){
                if( this.inspect.type === 'create' ){
                    return true;
                }
                else if( this.inspect.type === 'update' ){
                    return this.inspect.resident_id;
                }
                else if( this.inspect.type === 'skip' ){
                    return null;
                }
            },

            creating(){
                if( this.importManifest ){
                    return this.sortWithKeys( this.importManifest.creating, this.creatingSortBy, this.creatingSortDir );
                }
            },
            updating(){
                if( this.importManifest ){
                    return this.sortWithKeys( this.importManifest.updating, this.updatingSortBy, this.updatingSortDir );
                }
            },
            archiving(){
                if( this.importManifest ){
                    return this.sortWithKeys( this.importManifest.archiving, this.archivingSortBy, this.archivingSortDir );
                }
            },

            actualCreating(){
                if( this.importManifest ){
                    return _.reduce( this.importManifest.creating, ( carry, creatingResident, index ) => {
                        if( !(index in this.importManifest.exempt_data_ids) || this.importManifest.exempt_data_ids[ index ] !== null ){
                            carry[ index ] = creatingResident;
                        }

                        return carry;
                    }, {} );
                }

                return {};
            },
            actualUpdating(){
                if( this.importManifest ){
                    return _.reduce( this.importManifest.updating, ( carry, updatingResident, index ) => {
                        if(
                            !(index in this.importManifest.exempt_data_ids &&
                                this.importManifest.exempt_data_ids[ index ] === null) &&
                            !this.importManifest.exempt_resident_ids.includes( updatingResident.resident.id )
                        ){
                            carry[ index ] = updatingResident;
                        }

                        return carry;
                    }, {} );
                }

                return {};
            },
            actualArchiving(){
                if( this.importManifest ){
                    return _.reduce( this.importManifest.archiving, ( carry, archivingResident, index ) => {
                        if( !this.importManifest.exempt_resident_ids.includes( archivingResident.id ) ){
                            carry[ index ] = archivingResident;
                        }

                        return carry;
                    }, {} );
                }

                return {};
            },

            loadingAll(){
                return this.loading || (this.jobMonitor && this.jobMonitor.end_at === null && this.jobMonitor.error_at === null);
            },

            monitorProgress(){
                if( this.jobMonitor ){
                    return this.jobMonitor.progress;
                }
            }
        },
        watch: {
            "inspect.type"(){
                if( this.inspect.type === 'update' ){
                    this.inspect.loading = false;
                    this.loadInspectResidents()
                        .catch( this.$root.handleErrors )
                        .finally( () => {
                            this.inspect.loading = false;
                        } );
                }
            }
        },
        methods: {
            sortWithKeys( collection, sortBy, sortDirection ){
                return _.fromPairs( _.orderBy( _.toPairs( collection ), [ '1.' + sortBy ], [ _.lowerCase( sortDirection ) ] ) );
            },

            toggleShowCreating: _.throttle( function(){
                this.showCreating = !this.showCreating;
            }, 600 ),
            toggleShowUpdating: _.throttle( function(){
                this.showUpdating = !this.showUpdating;
            }, 600 ),
            toggleShowArchiving: _.throttle( function(){
                this.showArchiving = !this.showArchiving;
            }, 600 ),

            // Exempt Resident Ids
            toggleExemptResidentId( resident_id ){
                if( this.importManifest.exempt_resident_ids.includes( resident_id ) ){
                    var index = this.importManifest.exempt_resident_ids.indexOf( resident_id );
                    this.importManifest.exempt_resident_ids.splice( index, 1 );
                }
                else {
                    this.importManifest.exempt_resident_ids.push( resident_id );
                }
            },
            setAllExemptResidentIds( residents, index ){
                var exemptResidentIds = _.union( this.importManifest.exempt_resident_ids, _.map( residents, index ) );

                this.$set( this.importManifest, 'exempt_resident_ids', exemptResidentIds );
            },
            clearExemptResidentIds( residents, index ){
                var exemptResidentIds = _.difference( this.importManifest.exempt_resident_ids, _.map( residents, index ) );

                this.$set( this.importManifest, 'exempt_resident_ids', exemptResidentIds );
            },

            // Exempt Data Ids
            setExemptDataId( data_id, value ){
                this.$set( this.importManifest.exempt_data_ids, data_id, value );
            },
            unsetExemptDataId( data_id ){
                this.$delete( this.importManifest.exempt_data_ids, data_id );
            },
            setAllExemptDataIds( data ){
                _.each( Object.keys( data ), ( index ) => {
                    if( !(index in this.importManifest.exempt_data_ids) ){
                        this.setExemptDataId( index, null );
                    }
                } );
            },
            clearExemptDataIds( data ){
                _.each( Object.keys( data ), ( index ) => {
                    if( index in this.importManifest.exempt_data_ids && this.importManifest.exempt_data_ids[ index ] === null ){
                        this.unsetExemptDataId( index );
                    }
                } );
            },

            setImportManifest( importManifest ){
                if( _.isArray( importManifest.exempt_data_ids ) ){
                    importManifest.exempt_data_ids = _.reduce( importManifest.exempt_data_ids, ( carry, data_id ) => {
                        carry[ data_id ] = null;

                        return carry;
                    }, {} );
                }

                this.$set( this, 'importManifest', importManifest );
            },

            init(){
                this.loading = true;

                return this.loadImportManifest()
                    .then( () => {
                        return Promise.all( [
                            this.pollJobMonitor(),
                        ] );
                    } )
                    .then( () => {
                        if( !this.importManifest.hasImportColumns ){
                            this.$modal.show( 'import-columns-modal' );
                        }
                    } )
                    .finally( () => {
                        this.loading = false;
                    } );
            },

            loadImportManifest(){
                return axios.get( '/api/importManifests/' + this.importManifestId )
                    .then( ( result ) => {
                        this.setImportManifest( result.data.ImportManifest );
                    } )
                    .catch( this.$root.handleErrors );
            },

            saveImportManifest(){
                return this.$validator.validateAll( 'importManifest' )
                    .then( ( valid ) => {
                        if( valid ){
                            this.loading = true;

                            return axios.put( '/api/importManifests/' + this.importManifestId, {
                                ImportManifest: this.importManifest
                            } )
                                .then( () => {
                                    return axios.put( '/api/importManifests/' + this.importManifestId + '/sync' );
                                } )
                                .then( this.pollJobMonitor )
                                .then( () => {
                                    return this.loadImportManifest();
                                } )
                                .then( () => {
                                    this.$notify( {
                                        type: 'success',
                                        placement: 'top-left',
                                        title: 'Import Saved',
                                    } );
                                } )
                                .finally( () => {
                                    this.loading = false;
                                } )
                                .catch( this.$root.handleErrors );
                        }
                    } );
            },

            loadData(){
                this.loading = true;

                return axios.put( '/api/importManifests/' + this.importManifestId + '/load' )
                    .then( this.pollJobMonitor )
                    .then( () => {
                        return axios.put( '/api/importManifests/' + this.importManifestId + '/sync' );
                    } )
                    .then( this.pollJobMonitor )
                    .then( () => {
                        return this.loadImportManifest();
                    } )
                    .finally( () => {
                        this.loading = false;
                    } )
                    .catch( this.$root.handleErrors );
            },

            executeImport(){
                return this.$validator.validateAll()
                    .then( ( valid ) => {
                        if( valid ){
                            this.loading = true;

                            this.$modal.hide( 'confirm-execute' );

                            return axios.put( '/api/importManifests/' + this.importManifestId, {
                                ImportManifest: this.importManifest
                            } )
                                .then( () => {
                                    return axios.post( '/api/importManifests/' + this.importManifestId + '/apply' );
                                } )
                                .then( this.pollJobMonitor )
                                .finally( () => {
                                    window.location.reload();
                                } )
                                .catch( this.$root.handleErrors );
                        }
                    } );
            },

            deleteImport(){
                this.loading = true;

                var token = $( 'meta[name="csrf-token"]' ).attr( 'content' );
                var form = document.createElement( 'form' );
                form.setAttribute( 'method', 'POST' );
                form.setAttribute( 'action', '/importManifests/' + this.importManifest.id );

                var hiddenField = document.createElement( 'input' );
                hiddenField.setAttribute( 'type', 'hidden' );
                hiddenField.setAttribute( 'name', '_method' );
                hiddenField.setAttribute( 'value', 'DELETE' );

                var tokenField = document.createElement( 'input' );
                tokenField.setAttribute( 'type', 'hidden' );
                tokenField.setAttribute( 'name', '_token' );
                tokenField.setAttribute( 'value', token );

                form.appendChild( tokenField );
                form.appendChild( hiddenField );

                document.body.appendChild( form );

                form.submit();
            },

            syncImport(){
                this.loading = true;

                return axios.put( '/api/importManifests/' + this.importManifestId + '/sync' )
                    .then( this.pollJobMonitor )
                    .then( () => {
                        return this.loadImportManifest();
                    } )
                    .finally( () => {
                        this.loading = false;
                    } )
                    .catch( this.$root.handleErrors );
            },

            pollJobMonitor( promise ){
                var self = this;

                return new Promise( ( resolve, reject ) => {
                    let poll = function(){
                        self.loadJobMonitor()
                            .then( function( jobMonitor ){
                                self.$set( self, 'jobMonitor', jobMonitor );

                                if( jobMonitor && jobMonitor.end_at === null && jobMonitor.error_at === null ){
                                    setTimeout( poll, 1000 );
                                }
                                else {
                                    resolve( jobMonitor );
                                }
                            } )
                            .catch( reject );
                    };

                    poll();
                } );
            },

            loadJobMonitor(){
                var url = '/api/jobMonitors?' +
                    'sort_by=created_at&sort_dir=desc&limit=1&jobable_type=App\\ImportManifest&jobable_id=' + this.importManifestId;

                return axios.get( url )
                    .then( ( result ) => {
                        return Promise.resolve( _.head( result.data.JobMonitors ) );
                    } )
                    .catch( this.$root.handleErrors );
            },

            setInspect(){
                if( this.inspect.type === 'update' ){
                    return this.$validator.validateAll( 'inspect' )
                        .then( ( valid ) => {
                            if( valid ){
                                this.setExemptDataId( this.inspect.index, this.inspectValue );

                                this.$modal.hide( 'inspect-modal' );
                            }
                        } );
                }
                else {
                    this.setExemptDataId( this.inspect.index, this.inspectValue );

                    this.$modal.hide( 'inspect-modal' );
                }
            },

            unsetInspect(){
                this.unsetExemptDataId( this.inspect.index );

                this.$modal.hide( 'inspect-modal' );
            },

            setInspectResidentId( resident_id ){
                this.$set( this.inspect, 'resident_id', resident_id );
            },

            setInspectType( type ){
                this.inspect.type = type;

                if( type !== 'update' ){
                    this.setInspect();
                }
            },

            openInspectModal( index ){
                this.$set( this.inspect, 'index', index );

                let isCreating = (index in this.importManifest.exempt_data_ids &&
                    this.importManifest.exempt_data_ids[ index ] === true) ||
                    index in this.importManifest.creating;

                let isUpdating = (index in this.importManifest.exempt_data_ids &&
                    _.isString( this.importManifest.exempt_data_ids[ index ] )) ||
                    index in this.importManifest.updating;

                if( isCreating ){
                    this.$set( this.inspect, 'type', 'create' );
                }
                else if( isUpdating ){
                    this.$set( this.inspect, 'type', 'update' );
                    this.$set( this.inspect, 'resident_id', this.importManifest.updating[ index ].resident.id );
                }

                this.$nextTick( () => {
                    this.$modal.show( 'inspect-modal' );
                } );
            },

            loadInspectResidents(){
                let url = `/api/residents?project_id=${this.importManifest.project_id}&archived=true&limit=3&`;

                _.forEach( this.inspectData, ( { value, importColumn }, columnIndex ) => {
                    if( !importColumn.matchExempt && value !== null ){
                        let attribute = importColumn.modelName === 'resident' ?
                            importColumn.attributeName :
                            'leases.' + importColumn.attributeName;

                        url += `or[${columnIndex}][${attribute}]=${value}&`;

                    }
                } );

                url = _.trim( url, '&' );

                return axios.get( url )
                    .then( ( result ) => {
                        this.$set( this.inspect, 'residents', result.data.Residents );
                    } )
                    .catch( this.$root.handleErrors );
            },

            resetInspect(){
                Object.assign( this.$data.inspect, this.$options.data().inspect );
            },

            // Rows
            getRowColorObject( index ){
                return {
                    danger: index in this.importManifest.exempt_data_ids && this.importManifest.exempt_data_ids[ index ] === null,
                    warning: index in this.importManifest.exempt_data_ids && typeof this.importManifest.exempt_data_ids[ index ] === 'string',
                    success: index in this.importManifest.exempt_data_ids && this.importManifest.exempt_data_ids[ index ] === true
                };
            },

            // Sorting
            setSort( key, sortBy ){
                let sortByIndex = key + 'SortBy';
                let sortDirectionIndex = key + 'SortDir';

                if( this[ sortByIndex ] === sortBy ){
                    this[ sortDirectionIndex ] = (this[ sortDirectionIndex ] === 'ASC' ? 'DESC' : 'ASC');
                }
                else {
                    this[ sortByIndex ] = sortBy;
                    this[ sortDirectionIndex ] = 'ASC';
                }
            },

            willAutoEnroll( creatingResidentRow ){
                if( this.importManifest && this.importManifest.project.auto_enroll_at ){
                    var autoEnrollAt = moment.utc( this.importManifest.project.auto_enroll_at ).startOf( 'day' );
                    var moveInAt = moment.utc( creatingResidentRow.move_in_at ).startOf( 'day' );

                    return moveInAt.isSameOrAfter( autoEnrollAt );
                }

                return false;
            },

            resetData(){
                Object.assign( this.$data, this.$options.data() );
            },
        }
    }
</script>
